import { getDocumentFirstFileId } from 'libs/core/hooks/src/Document/useDocumentFirstFile';
import React, { createContext, useCallback, useContext } from 'react';
import type { AxiosResponse } from 'axios';
import {
	extractOfDataQuery,
	sendSubmissionQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { GovError, useExtraConfigContext } from '@gov-nx/core/app';
import { useDownloadEvents, useMessageEvents } from '@gov-nx/core/events';
import {
	useContactDetailsByReza,
	useLastDocument,
	useProcessControl,
	useServiceAuthorization,
} from '@gov-nx/core/hooks';
import {
	getRequiredDataBoxes,
	useCountry,
	useTranslationWithNamespace,
} from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { useDataBoxAccess } from '@gov-nx/module/data-box';
import { useDataBoxStoreLists } from '@gov-nx/store';
import {
	getApiWarningRequestHeader,
	useBoolean,
	WarningRequestHeader,
} from '@gov-nx/utils/common';
import type { ServiceCode } from '../definitions/codes';
import {
	FormInstance,
	prepareSubmitData,
	prepareSubmitDataDS,
} from './FormDefinitions';
import type {
	FormData,
	ServiceContextControls,
	ServiceContextTypes,
} from './service.types';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';

export const ServiceContext =
	createContext<Nullable<ServiceContextTypes>>(null);

export interface ServiceContextProviderProps {
	children: React.ReactNode;
	code: ServiceCode;
}

export function VypisZRejstrikuTrestuPOContextProvider({
	children,
	code,
}: ServiceContextProviderProps) {
	const { toastMessageError, toastMessageSuccess } = useMessageEvents();
	const changeEmail = useBoolean();
	const {
		extraConfig: { application },
	} = useExtraConfigContext();
	const contactDetails = useContactDetailsByReza({
		isBusinessPersonAllowed: application === 'pp',
	});
	const requiredDataBoxes = getRequiredDataBoxes(code, application);
	const { getConnectedDataBoxesByTypes } = useDataBoxStoreLists();
	const dataBoxes = getConnectedDataBoxesByTypes(requiredDataBoxes);
	const { downloadDocument } = useDownloadEvents();
	const { controls, setControls } = useProcessControl<ServiceContextControls>({
		mustWaitForProcessing: null,

		sendToDSLoading: false,
		sendToDSError: null,
	});
	const authContext = useAutorizaceContext();
	const AUTHORIZATION_SERVICE_NAME = 'REJSTRIK_TRESTU_PO';
	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);

	const { formDefinition, getFields } = FormInstance({
		code,
		isEmail: !!contactDetails?.email,
		email: contactDetails?.email,
		dataBoxTypes: requiredDataBoxes,
		isLowAuthorization,
	});
	const registeredLocation = formDefinition.formMethods.watch(
		'_registeredLocation'
	);
	const enabled = dataBoxes.length > 0 && !!registeredLocation;
	const lastDocument = useLastDocument(code, enabled, {
		typOsoby: 'PO',
		poZahranicni: registeredLocation === 'foreign',
	});
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);
	const { filteredAllCountriesToSelect } = useCountry({ filterOut: ['cz'] });
	const { axiosInstance } = useAxiosInstance();

	const { areAnyConnected } = useDataBoxAccess({
		requiredTypes: requiredDataBoxes,
	});

	const submitMutationDS = usePoMutation<AxiosResponse<void>, FormData>({
		mutationFn: async (data: FormData) => {
			const prepared = prepareSubmitDataDS(data);
			return extractOfDataQuery(axiosInstance)(prepared);
		},
		onError: (error: Error) => {
			setControls({ sendToDSError: error, sendToDSLoading: false });
		},
		onSuccess: (response) => {
			formDefinition.formReset();
			setControls({ sendToDSLoading: false });

			const nonHighAuthorizationFlow = () => {
				toastMessageSuccess(tsn('formular.zprava.odeslano'));
			};

			if (authContext) {
				authContext.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME,
					response.headers['x-autorizaceid'],
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
	});

	const submitMutation = usePoMutation({
		mutationFn: async (data: FormData) => {
			const prepared = prepareSubmitData(contactDetails?.email)(data);
			return sendSubmissionQuery(axiosInstance)(prepared);
		},
		onError: (error: Error) => {
			setControls({ processError: error, processLoading: false });
		},
		onSuccess: async (response) => {
			const headerWarning = getApiWarningRequestHeader(response);
			if (headerWarning) {
				setControls({
					mustWaitForProcessing: headerWarning,
					processLoading: false,
				});
				await lastDocument.invalidateCache();
				formDefinition.formReset();
			} else if (response.data > 0) {
				const souborId = await getDocumentFirstFileId(axiosInstance, {
					documentId: response.data,
					onError: () =>
						toastMessageError(tsn('formular.zprava.chyba-pri-odesilani')),
				});

				if (!souborId) {
					return;
				}

				downloadDocument({
					documentId: response.data,
					souborId,
					callback: async () => {
						setControls({ processLoading: false });
						await lastDocument.invalidateCache();
						formDefinition.formReset();
					},
				});
			} else if (response.data === 0) {
				await lastDocument.invalidateCache();
				setControls({
					mustWaitForProcessing: WarningRequestHeader.UNKNOW,
					processLoading: false,
				});
				formDefinition.formReset();
			} else {
				toastMessageError(tsn('formular.zprava.chyba-pri-odesilani'));
				await lastDocument.invalidateCache();
				setControls({
					processLoading: false,
					processError: new GovError(
						`[Service]: An unexpected error has occurred in (${code})`
					),
				});
			}
		},
	});

	const handleSubmit = useCallback(async () => {
		setControls({ processError: null, processLoading: true });
		submitMutation.mutate(formDefinition.formMethods.getValues());
	}, [formDefinition.formMethods, setControls, submitMutation]);

	const handleSubmitDS = useCallback(async () => {
		setControls({ sendToDSError: null, sendToDSLoading: true });
		submitMutationDS.mutate(formDefinition.formMethods.getValues());
	}, [formDefinition.formMethods, setControls, submitMutationDS]);

	const isProcessing =
		submitMutation.isLoading ||
		controls.processLoading ||
		controls.sendToDSLoading;

	return (
		<ServiceContext.Provider
			value={{
				code,
				controls,
				formDefinition,
				fields: getFields(isProcessing),
				onSubmit: formDefinition.formMethods.handleSubmit(handleSubmit),
				onSendToDS: formDefinition.formMethods.handleSubmit(handleSubmitDS),
				submitMutation,
				setControls,
				isEmail: !!contactDetails?.email,
				email: contactDetails?.email,
				changeEmail,
				lastDocument,
				areAnyDataBoxesConnected: areAnyConnected,
				isProcessing,
				registeredLocation,
				countriesToSelect: filteredAllCountriesToSelect,
				isLowAuthorization,
				isAuthorizationEnforced,
			}}>
			{children}
		</ServiceContext.Provider>
	);
}

export const useVypisRejstrikuTrestuPOContext = (): ServiceContextTypes =>
	useContext(ServiceContext) as ServiceContextTypes;
